import { useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { getMyEvents, deleteEvent, setEventEnabled, promoteEvent, getNotifyReach, notifyCandidates, EventResponse, NotifyReach, isEventExpired, isEventDateExpired, formatEventRange } from "../../services/event-service"
import { getGigsByEvent, deleteGig } from "../../services/gig-service"
import { GigPreview } from "../../hooks/models/GigPreview"
import ConfirmModal from "../../components/confirmModal"
import PromoteDurationModal from "../../components/promoteDurationModal"
import { useCredits } from "../../context/credits"
import { default as ModalProvider } from "react-modal"

const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" })
}

const rateTypeLabel: Record<string, string> = {
    HOURLY: "/ oră",
    DAILY: "/ zi",
    MONTHLY: "/ lună",
    FIXED: "fix",
}

export const EventDetailsPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const location = useLocation()
    const { balance, pricing, refresh: refreshCredits } = useCredits()

    const [event, setEvent] = useState<EventResponse | null>(null)
    const [gigs, setGigs] = useState<GigPreview[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const [pendingDeleteGig, setPendingDeleteGig] = useState<GigPreview | null>(null)
    const [eventToggling, setEventToggling] = useState(false)
    const [eventDeleting, setEventDeleting] = useState(false)
    const [pendingDeleteEvent, setPendingDeleteEvent] = useState(false)
    const [notice, setNotice] = useState<string | null>(null)
    const [flash, setFlash] = useState<string | null>(null)
    const [showPromote, setShowPromote] = useState(false)
    const [promoting, setPromoting] = useState(false)
    const [reach, setReach] = useState<NotifyReach | null>(null)
    const [showNotify, setShowNotify] = useState(false)
    const [notifying, setNotifying] = useState(false)

    useEffect(() => {
        if (!id) return
        loadData()
    }, [id])

    useEffect(() => {
        const msg = (location.state as { flash?: string } | null)?.flash
        if (msg) {
            setFlash(msg)
            navigate(location.pathname, { replace: true, state: null })
        }
    }, [location, navigate])

    useEffect(() => {
        if (!flash) return
        const t = setTimeout(() => setFlash(null), 4000)
        return () => clearTimeout(t)
    }, [flash])

    useEffect(() => {
        if (!notice) return
        const t = setTimeout(() => setNotice(null), 6000)
        return () => clearTimeout(t)
    }, [notice])

    const loadData = async () => {
        setIsLoading(true)
        try {
            const [events, gigsData] = await Promise.all([
                getMyEvents(),
                getGigsByEvent(id!),
            ])
            const found = events.find(ev => ev.id === id) ?? null
            setEvent(found)
            setGigs(Array.isArray(gigsData) ? gigsData : [])
        } catch {
            // ignore
        }
        setIsLoading(false)
    }

    const handleDeleteGig = async (gigId: string) => {
        setDeletingId(gigId)
        try {
            await deleteGig(gigId)
            setGigs(prev => prev.filter(g => g.id !== gigId))
            setPendingDeleteGig(null)
        } catch {
            // ignore
        }
        setDeletingId(null)
    }

    const handleToggleEvent = async () => {
        if (!event) return
        const next = event.enabled === false
        if (next && isEventDateExpired(event)) {
            setNotice("Evenimentul a expirat și nu poate fi reactivat direct. Apasă pe „Editează” și schimbă perioada pentru a-l publica din nou.")
            return
        }
        setNotice(null)
        setEventToggling(true)
        try {
            const updated = await setEventEnabled(event.id, next)
            setEvent(updated)
        } catch (e: any) {
            setNotice(e?.message || "A apărut o eroare. Încearcă din nou.")
        }
        setEventToggling(false)
    }

    const handleDeleteEvent = async () => {
        if (!event) return
        setEventDeleting(true)
        try {
            await deleteEvent(event.id)
            navigate("/events", { state: { flash: "Evenimentul a fost șters." } })
        } catch (e: any) {
            setEventDeleting(false)
            setPendingDeleteEvent(false)
            setNotice(e?.message || "Nu am putut șterge evenimentul. Încearcă din nou.")
        }
    }

    const handlePromote = async (durationDays: number) => {
        if (!event) return
        setPromoting(true)
        try {
            const updated = await promoteEvent(event.id, durationDays)
            setEvent(updated)
            setShowPromote(false)
            refreshCredits()
            setFlash("Evenimentul este acum promovat. 🚀")
        } catch (e: any) {
            if (e?.code === "insufficient.credits") { navigate("/credits"); return }
            setNotice(e?.message || "A apărut o eroare. Încearcă din nou.")
            setShowPromote(false)
        }
        setPromoting(false)
    }

    const handleStopPromote = async () => {
        if (!event) return
        setPromoting(true)
        try {
            const updated = await promoteEvent(event.id, 0)
            setEvent(updated)
        } catch (e: any) {
            setNotice(e?.message || "A apărut o eroare. Încearcă din nou.")
        }
        setPromoting(false)
    }

    const openNotify = async () => {
        if (!event) return
        if (!event.cityName) {
            setNotice("Adaugă un oraș evenimentului pentru a putea notifica candidații din zonă.")
            return
        }
        if (event.lastNotifiedAt && (Date.now() - new Date(event.lastNotifiedAt).getTime()) < 24 * 60 * 60 * 1000) {
            setNotice("Ai notificat candidații recent. Poți trimite o nouă notificare peste 24 de ore.")
            return
        }
        setShowNotify(true)
        setReach(null)
        try {
            setReach(await getNotifyReach(event.id))
        } catch {
            setReach({ reach: 0, cost: 0 })
        }
    }

    const handleNotify = async () => {
        if (!event) return
        setNotifying(true)
        try {
            const res = await notifyCandidates(event.id)
            setEvent({ ...event, lastNotifiedAt: new Date().toISOString() })
            setShowNotify(false)
            setFlash(`Notificare trimisă către ${res.reach} candidați. 📣`)
        } catch (e: any) {
            if (e?.code === "insufficient.credits") { navigate("/credits"); return }
            setShowNotify(false)
            setNotice(e?.message || "A apărut o eroare. Încearcă din nou.")
        }
        setNotifying(false)
    }

    if (isLoading) {
        return (
            <div className="flex flex-col h-full animate-pulse">
                <div className="h-52 w-full bg-figmaLightGray" />
                <div className="px-4 py-4 sm:px-6 sm:py-5">
                    <div className="bg-white rounded-2xl p-4 border border-figmaLightGray flex flex-col gap-3">
                        <div className="flex gap-2">
                            <div className="h-7 w-40 rounded-lg bg-figmaLightGray" />
                            <div className="h-7 w-28 rounded-lg bg-figmaLightGray" />
                        </div>
                        <div className="h-3 w-full rounded bg-figmaLightGray" />
                        <div className="h-3 w-3/4 rounded bg-figmaLightGray" />
                        <div className="mt-2 h-10 w-full rounded-xl bg-figmaLightGray" />
                    </div>
                </div>
                <div className="px-4 sm:px-6 flex flex-col gap-3">
                    <div className="h-5 w-24 rounded bg-figmaLightGray" />
                    {[0, 1].map(i => <div key={i} className="h-20 w-full rounded-2xl bg-figmaLightGray" />)}
                </div>
            </div>
        )
    }

    if (!event) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-figmaDarkGray">
                <p className="text-lg font-semibold">Evenimentul nu a fost găsit.</p>
                <button onClick={() => navigate("/events")} className="mt-4 text-figmaPrimary font-semibold text-sm">
                    ← Înapoi la evenimente
                </button>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="relative flex-shrink-0">
                {event.imageUrl ? (
                    <img
                        src={event.imageUrl}
                        alt={event.title}
                        loading="lazy"
                        className="w-full h-52 object-cover"
                    />
                ) : (
                    <div className="flex h-52 w-full items-center justify-center bg-gradient-to-br from-figmaPrimary/10 to-figmaPrimary/30 text-5xl">🗓️</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <button
                    onClick={() => navigate("/events")}
                    aria-label="Înapoi"
                    className="absolute top-4 left-4 bg-white/90 hover:bg-white text-figmaBlack rounded-full w-9 h-9 flex items-center justify-center shadow-md backdrop-blur-sm transition-colors"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                        <path d="m15 18-6-6 6-6" />
                    </svg>
                </button>

                {/* acțiuni eveniment (sus-dreapta) — creion + coș */}
                <div className="absolute top-4 right-4 flex items-center gap-2">
                    <button
                        onClick={() => navigate(`/events/${event.id}/edit`, { state: { from: `/events/${event.id}` } })}
                        aria-label="Editează evenimentul"
                        title="Editează"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-figmaPrimary shadow-md backdrop-blur-sm transition-colors hover:bg-figmaPrimary hover:text-white"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => setPendingDeleteEvent(true)}
                        aria-label="Șterge evenimentul"
                        title="Șterge"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-figmaContrast shadow-md backdrop-blur-sm transition-colors hover:bg-figmaContrast hover:text-white"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]">
                            <path d="M3 6h18" />
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                        </svg>
                    </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        {event.promoted && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-figmaPrimary bg-blue-100 px-2 py-1 rounded-md">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="h-3 w-3"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" /></svg>
                                Promovat
                            </span>
                        )}
                        {event.cityName && (
                            <span className="inline-block text-xs font-bold text-white bg-figmaPrimary px-2 py-1 rounded-md">
                                {event.cityName}{event.countyCode ? ` (${event.countyCode})` : ""}
                            </span>
                        )}
                        {isEventExpired(event) && (
                            <span className={`inline-flex items-center gap-1.5 text-xs font-bold text-white px-2 py-1 rounded-md ${event.enabled === false ? "bg-figmaDarkGray" : "bg-figmaContrast"}`}>
                                <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                {event.enabled === false ? "Inactiv" : "Expirat"}
                            </span>
                        )}
                    </div>
                    <h2 className="text-white font-extrabold text-xl leading-tight">{event.title}</h2>
                </div>
            </div>

            {/* Info card */}
            <div className="px-4 py-4 sm:px-6 sm:py-5 flex-shrink-0">
                <div className="bg-white rounded-2xl p-4 border border-figmaLightGray flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-figmaLightGray px-2.5 py-1.5 text-xs font-medium text-figmaBlack">
                            <svg viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <path d="M16 2v4M8 2v4M3 10h18" />
                            </svg>
                            {formatEventRange(event.dateFrom, event.dateTo)}
                        </span>
                        {(event.cityName || event.address) && (
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-figmaLightGray px-2.5 py-1.5 text-xs font-medium text-figmaBlack">
                                <svg viewBox="0 0 24 24" fill="none" stroke="#486af6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                {[event.address, event.cityName && `${event.cityName}${event.countyCode ? ` (${event.countyCode})` : ""}`].filter(Boolean).join(", ")}
                            </span>
                        )}
                    </div>
                    {event.description && (
                        <p className="text-sm text-figmaBlack leading-relaxed">{event.description}</p>
                    )}

                    <div className="mt-2 border-t border-figmaLightGray pt-3">
                        <button
                            type="button"
                            onClick={handleToggleEvent}
                            disabled={eventToggling}
                            className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
                                event.enabled === false
                                    ? "bg-figmaPrimary text-white hover:opacity-90"
                                    : "bg-figmaLightGray text-figmaDarkGray hover:bg-figmaDarkBg/30 hover:text-figmaBlack"
                            }`}
                        >
                            {eventToggling ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                                <>
                                    <span className={`h-2 w-2 rounded-full ${event.enabled === false ? "bg-white" : "bg-figmaDarkGray"}`} />
                                    {event.enabled === false ? "Activează evenimentul" : "Dezactivează evenimentul"}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Monetizare: promovare / notificare / republicare */}
            {isEventDateExpired(event) ? (
                <div className="px-4 pb-1 sm:px-6 flex-shrink-0">
                    <div className="bg-white rounded-2xl p-4 border border-figmaDarkBg">
                        <p className="text-sm font-bold text-figmaBlack">Acest eveniment a expirat</p>
                        <p className="text-xs text-figmaDarkGray mt-1">Republică-l cu o perioadă nouă ca să fie vizibil din nou pentru candidați.</p>
                        <button
                            type="button"
                            onClick={() => navigate(`/events/${event.id}/edit`, { state: { from: `/events/${event.id}`, republish: true } })}
                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                        >
                            ♻️ Republică evenimentul — 20 credite
                        </button>
                    </div>
                </div>
            ) : !isEventExpired(event) ? (
                <div className="px-4 pb-1 sm:px-6 flex-shrink-0">
                    <div className="bg-white rounded-2xl p-4 border border-figmaLightGray">
                        <div className="flex items-center gap-2 mb-3">
                            <svg viewBox="0 0 24 24" fill="#486af6" className="h-4 w-4"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" /></svg>
                            <h4 className="text-sm font-bold text-figmaBlack">Crește vizibilitatea</h4>
                        </div>

                        {event.promoted ? (
                            <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
                                <p className="text-sm font-semibold text-figmaBlack">
                                    Promovat{event.promotedUntil ? ` până la ${formatDate(event.promotedUntil)}` : ""}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleStopPromote}
                                    disabled={promoting}
                                    className="text-xs font-semibold text-figmaContrast hover:underline disabled:opacity-50 shrink-0"
                                >
                                    {promoting ? "..." : "Oprește"}
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowPromote(true)}
                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" /></svg>
                                Promovează evenimentul
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={openNotify}
                            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-figmaLightGray px-4 py-2.5 text-sm font-semibold text-figmaBlack transition-colors hover:bg-figmaDarkBg/30"
                        >
                            📣 Notifică candidații din zonă
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Jobs section */}
            <div className="flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-extrabold text-figmaBlack text-lg">Joburi</h3>
                        <p className="text-xs text-figmaDarkGray">{gigs.length} {gigs.length === 1 ? "job" : "joburi"} pentru acest eveniment</p>
                    </div>
                    <button
                        onClick={() => {
                            const params = new URLSearchParams({
                                eventId: event.id,
                                eventTitle: event.title,
                                eventDateFrom: event.dateFrom,
                                eventDateTo: event.dateTo,
                                ...(event.cityName ? { eventCityName: event.cityName } : {}),
                                ...(event.countyCode ? { eventCounty: event.countyCode } : {}),
                            })
                            navigate(`/job?${params.toString()}`)
                        }}
                        className="bg-figmaPrimary text-white font-bold rounded-2xl px-4 py-2 text-sm hover:opacity-90 transition-opacity"
                    >
                        + Adaugă job
                    </button>
                </div>

                {gigs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-figmaDarkGray border-2 border-dashed border-figmaLightGray rounded-2xl">
                        <p className="font-semibold">Niciun job adăugat</p>
                        <p className="text-sm mt-1">Folosește butonul de mai sus pentru a adăuga primul job.</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {gigs.map(gig => (
                            <div
                                key={gig.id}
                                role="button"
                                tabIndex={0}
                                aria-label={`Editează jobul ${gig.gigTitle}`}
                                onClick={() => navigate(`/job/${gig.id}`)}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/job/${gig.id}`) } }}
                                className="bg-white rounded-2xl p-4 border border-figmaLightGray shadow-sm flex items-center justify-between gap-3 cursor-pointer transition-all duration-200 hover:border-figmaPrimary hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-figmaPrimary focus-visible:ring-offset-2"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-figmaBlack text-sm truncate">{gig.gigTitle}</p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                        {gig.location && (
                                            <span className="flex items-center gap-1 text-xs text-figmaDarkGray">
                                                <img src="/images/navigation.svg" alt="locație" className="w-[12px] h-[12px] shrink-0" />
                                                {gig.location}
                                            </span>
                                        )}
                                        {gig.rate != null && !gig.confidentialRate ? (
                                            <span className="flex items-center gap-1 text-xs text-figmaDarkGray">
                                                <img src="/images/ic-dollar.svg" alt="salariu" className="w-[12px] h-[12px] shrink-0" />
                                                {gig.rate} RON {rateTypeLabel[gig.rateType] ?? ""}
                                            </span>
                                        ) : gig.confidentialRate ? (
                                            <span className="flex items-center gap-1 text-xs text-figmaDarkGray">
                                                <img src="/images/ic-dollar.svg" alt="salariu" className="w-[12px] h-[12px] shrink-0" />
                                                Salariu confidențial
                                            </span>
                                        ) : null}
                                        {gig.dateFrom && (
                                            <span className="flex items-center gap-1 text-xs text-figmaDarkGray">
                                                <img src="/images/calendar-icon.svg" alt="data" className="w-[12px] h-[12px] shrink-0" />
                                                {formatDate(gig.dateFrom)}{gig.dateTo ? ` — ${formatDate(gig.dateTo)}` : ""}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        aria-label="Editează job"
                                        title="Editează"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/job/${gig.id}`) }}
                                        className="flex h-9 w-9 items-center justify-center rounded-full bg-figmaLightGray text-figmaPrimary transition-colors hover:bg-figmaPrimary hover:text-white"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[17px] w-[17px]">
                                            <path d="M12 20h9" />
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                        </svg>
                                    </button>
                                    <button
                                        aria-label="Șterge job"
                                        title="Șterge"
                                        onClick={(e) => { e.stopPropagation(); setPendingDeleteGig(gig) }}
                                        disabled={deletingId === gig.id}
                                        className="flex h-9 w-9 items-center justify-center rounded-full bg-figmaLightGray text-figmaContrast transition-colors hover:bg-figmaContrast hover:text-white disabled:opacity-50"
                                    >
                                        {deletingId === gig.id ? (
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[17px] w-[17px]">
                                                <path d="M3 6h18" />
                                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                                <path d="M10 11v6M14 11v6" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {flash && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-white border border-figmaPrimary rounded-2xl px-5 py-4 shadow-lg text-figmaBlack text-sm font-medium flex items-center gap-2">
                    <span className="text-figmaPrimary">✅</span> {flash}
                </div>
            )}

            {notice && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-white border border-figmaDarkGray rounded-2xl px-5 py-4 shadow-lg text-figmaBlack text-sm font-medium flex items-start justify-between gap-3">
                    <span>ℹ️ {notice}</span>
                    <button
                        type="button"
                        onClick={() => setNotice(null)}
                        className="text-figmaDarkGray hover:text-figmaBlack font-bold shrink-0"
                        aria-label="Închide"
                    >
                        ✕
                    </button>
                </div>
            )}

            <ConfirmModal
                isOpen={!!pendingDeleteGig}
                title="Ștergi acest job?"
                message={pendingDeleteGig ? `„${pendingDeleteGig.gigTitle}” va fi șters definitiv. Această acțiune nu poate fi anulată.` : undefined}
                confirmText="Șterge"
                loading={!!pendingDeleteGig && deletingId === pendingDeleteGig.id}
                onConfirm={() => pendingDeleteGig && handleDeleteGig(pendingDeleteGig.id)}
                onCancel={() => setPendingDeleteGig(null)}
            />

            <ConfirmModal
                isOpen={pendingDeleteEvent}
                title="Ștergi acest eveniment?"
                message={
                    gigs.length > 0
                        ? `Acest eveniment are ${gigs.length} ${gigs.length === 1 ? "job asociat" : "joburi asociate"}. Dacă îl ștergi, ${gigs.length === 1 ? "și acel job va deveni inaccesibil" : "și acele joburi vor deveni inaccesibile"}. Această acțiune nu poate fi anulată.`
                        : `„${event.title}” va fi șters definitiv. Această acțiune nu poate fi anulată.`
                }
                confirmText="Șterge oricum"
                loading={eventDeleting}
                onConfirm={handleDeleteEvent}
                onCancel={() => setPendingDeleteEvent(false)}
            />

            <PromoteDurationModal
                isOpen={showPromote}
                title="Promovează evenimentul"
                subtitle={`„${event.title}” va apărea sus în lista candidaților și în secțiunea de evenimente recomandate.`}
                tiers={pricing?.promoteEvent ?? []}
                balance={balance}
                loading={promoting}
                onConfirm={handlePromote}
                onCancel={() => setShowPromote(false)}
            />

            <ModalProvider
                appElement={document.getElementById("root")!}
                isOpen={showNotify}
                onRequestClose={() => { if (!notifying) setShowNotify(false) }}
                className="mx-4 my-auto w-full max-w-[440px] rounded-2xl bg-white outline-none"
                overlayClassName="fixed inset-0 z-[60] flex items-center justify-center bg-gray-800/60 p-4"
            >
                <div className="p-6 sm:p-7">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-figmaPrimary/10 text-2xl">📣</div>
                    <h3 className="mt-4 text-center text-lg font-bold text-figmaBlack">Notifică candidații</h3>
                    {reach === null ? (
                        <div className="my-6 flex justify-center">
                            <span className="h-6 w-6 animate-spin rounded-full border-2 border-figmaPrimary border-t-transparent" />
                        </div>
                    ) : reach.reach === 0 ? (
                        <p className="mt-2 text-center text-sm text-figmaDarkGray">
                            Nu există candidați de notificat în zona acestui eveniment momentan.
                        </p>
                    ) : (
                        <p className="mt-2 text-center text-sm text-figmaDarkGray leading-relaxed">
                            Vei trimite o notificare push către <span className="font-bold text-figmaBlack">~{reach.reach} candidați</span> din zona evenimentului
                            {event.cityName ? ` (${event.cityName})` : ""}.
                        </p>
                    )}

                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={() => setShowNotify(false)}
                            disabled={notifying}
                            className="flex-1 rounded-xl bg-figmaLightGray px-4 py-3 text-sm font-semibold text-figmaBlack transition-colors hover:bg-figmaDarkBg/30 disabled:opacity-50"
                        >
                            Anulează
                        </button>
                        <button
                            type="button"
                            onClick={handleNotify}
                            disabled={notifying || reach === null || reach.reach === 0}
                            className="flex flex-[1.4] items-center justify-center gap-2 rounded-xl bg-figmaPrimary px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                            {notifying ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : reach && reach.reach > 0 ? `Trimite — ${reach.cost} credite` : "Trimite"}
                        </button>
                    </div>
                </div>
            </ModalProvider>
        </div>
    )
}
